import { menuApi } from '@/api/restaurant/menuApi';
import { type MenuSection } from '@/models/restaurant/MenuSection';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { DataGrid, type GridColDef, type GridRowParams, type GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import type { Menu } from '../../models/restaurant/Menu';
import { fillHeight } from "../../theme";
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import Dialog from '../common/dialog/Dialog';
import { useToast } from '../common/toast/ToastProvider';

const MenuList = () => {    
    const [selectedMenuId, setSelectedMenuId] = useState<string | undefined>();
    const [menuDialogOpen, setMenuDialogOpen] = useState(false);
    const [menuDialogTitleName, setMenuDialogTitleName] = useState<string | undefined>();
    const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
    const [sectionDialogTitleName, setSectionDialogTitleName] = useState<string | undefined>();
    const { reset: resetMenu, register: registerMenu, handleSubmit: onMenuFormSubmit, formState: menuFormState } = useForm<Menu>();
    const { reset: resetSection, register: registerSection, handleSubmit: onSectionFormSubmit, formState: sectionFormState } = useForm<MenuSection>();
    const qc = useQueryClient();
    const app = useAppContext();
    const { data: menus, isPending: isGridLoading } = useQuery<Menu[]>({ queryKey: ['menus', app.locationId], enabled: app.locationId !== null, queryFn: () => menuApi.list(app.locationId!), staleTime: Infinity });
    const { mutateAsync: saveMenu, isPending: menuIsSaving } = useMutation({ mutationFn: (menu: Menu) => menuApi.update(app.locationId!, menu), onSuccess: () => handleSaveMenuSuccess(), onError: () => handleSaveMenuError() });
    const { mutateAsync: saveSection, isPending: sectionIsSaving } = useMutation({ mutationFn: (section: MenuSection) => menuApi.updateSection(app.locationId!, selectedMenuId!, section), onSuccess: (_data, variables, _context) => handleSaveSectionSuccess(variables), onError: () => handleSaveSectionError() });
    //const { mutateAsync: toggleSectionHidden, isPending: isSaving } = useMutation({ mutationFn: menuApi.toggleSectionHidden, onSuccess: () => handleToggleSuccess(), onError: () => handleToggleError() }); //react-query to toggle the selected section hidden
    const toast = useToast();
    const strings = useLocalizedStrings();

    // Menu
    
    const menuColumns: GridColDef<Menu>[] = [
        {
            field: 'name',
            headerName: strings.name,
            flex: 2,
        },
        {
            field: 'enabled',
            headerName: strings.enabled,
            type: 'boolean',
            flex: 1
        },
        {
            field: 'actions',
            headerName: strings.actions,
            type: 'actions',
            flex: 1,
            getActions: (params: GridRowParams) => [
                <DataGridEditButton id={params.id.toString()} onClick={handleEditMenuClick} aria-label="edit menu" />,
            ],
        },
    ];

    // Use this as opposed to storing the selectedMenu in state to ensure it updates when menus data changes
    const selectedMenu = useMemo(() => menus?.find(m => m.id === selectedMenuId), [menus, selectedMenuId]);

    const handleRowSelectionModelChange = ({ ids }: GridRowSelectionModel) => {
        const id = ids.values().next().value as string;
        if (!id) return;
        setSelectedMenuId(id); // Only store the selected menu id (useMemo will handle getting the selected menu object)
    };

    const handleEditMenuClick = (id: string) => {
        const menu = menus?.find(m => m.id === id);
        if (!menu) return;
        resetMenu(menu);
        setMenuDialogTitleName(menu.name);
        setMenuDialogOpen(true);
    };

    const handleSubmitMenu: SubmitHandler<Menu> = async (data) => {
        console.log(data);
        await saveMenu(data);
    }

    const handleSaveMenuSuccess = async () => {
        toast({ severity: "success", message: strings.menuSaveSuccess });
        await qc.invalidateQueries({ queryKey: ['menus', app.locationId] });
        setMenuDialogOpen(false);
    }

    const handleSaveMenuError = () => {
        toast({ severity: "error", message: strings.menuSaveError });
    }

    // Section

    const sectionColumns: GridColDef<MenuSection>[] = [
        {
            field: 'name',
            headerName: strings.name,
            flex: 2,
        },
        {
            field: 'enabled',
            headerName: strings.enabled,
            type: 'boolean',
            flex: 1
        },
        {
            field: 'actions',
            headerName: strings.actions,
            type: 'actions',
            flex: 1,
            getActions: (params: GridRowParams) => [
                <DataGridEditButton id={params.id.toString()} onClick={handleEditSectionClick} aria-label="edit menu" />,
            ],
        },
    ];

    const handleEditSectionClick = (id: string) => {
        const selectedMenu = menus?.find(m => m.id === selectedMenuId);
        if (!selectedMenu) return;
        const section = selectedMenu?.sections?.find((s: MenuSection) => s.id === id);
        if (!section) return;
        resetSection(section);
        setSectionDialogTitleName(section.name);
        setSectionDialogOpen(true);
    };

    const handleSubmitSection: SubmitHandler<MenuSection> = async (data) => {
        console.log(data);
        await saveSection(data);
    }

    const handleSaveSectionSuccess = async (updatedSection: MenuSection) => {
        toast({ severity: "success", message: strings.menuSectionSaveSuccess });

        // Update the menu section in the query cache to avoid a full refetch
        qc.setQueryData<Menu[]>(['menus', app.locationId!], (old) => {
            if (!old) return old;

            // Find and update all instances of the updated section (in all menus)
            return old.map(oldMenu => ({
                ...oldMenu,
                sections: oldMenu.sections.map((oldSection: MenuSection) =>
                    oldSection.id === updatedSection.id
                        ? { ...oldSection, ...updatedSection } // Merge the new data
                        : oldSection
                ),
            }));
        });
         
        setSectionDialogOpen(false);
    }

    const handleSaveSectionError = () => {
        toast({ severity: "error", message: strings.menuSectionSaveError });
    }

    return (
        <>
            <Box sx={{ ...fillHeight, p: 2, overflow: "hidden" }}>
                <Stack direction="row" spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <DataGrid
                            rows={menus}
                            columns={menuColumns}
                            onRowSelectionModelChange={handleRowSelectionModelChange}
                            autoPageSize={true}
                            loading={isGridLoading}
                            localeText={{ noRowsLabel: strings.menusNotFound }}
                        />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <DataGrid
                            rows={selectedMenu?.sections}
                            columns={sectionColumns}
                            autoPageSize={true}
                            localeText={{ noRowsLabel: !selectedMenu ? strings.selectMenu : strings.menuSectionsNotFound }}
                        />
                    </Box>
                </Stack>
            </Box>

            <form onSubmit={onMenuFormSubmit(handleSubmitMenu)} id="dialog-menu-form">
                <Dialog
                    open={menuDialogOpen}
                    title={strings.menuEditTitle.replace("{{name}}", menuDialogTitleName ?? "")}
                    formId="dialog-menu-form"
                    formIsDirty={menuFormState.isDirty}
                    saving={menuIsSaving}
                    saveButtonTitle={strings.save}
                    onCancel={() => setMenuDialogOpen(false)}
                    cancelButtonTitle={strings.cancel}
                >
                    {/* Setup TextField, the ...register function registers the field with react-hook-form (including validation) */}
                    <TextField
                        id="menu-name"
                        label={strings.name}
                        variant="outlined"
                        sx={{ width: '400px' }}
                        {...registerMenu("name", {
                            required: strings.validationNameRequired
                        })}
                        error={!!menuFormState.errors.name}
                        helperText={menuFormState.errors.name?.message || " "}
                    />
                </Dialog>
            </form>

            <form onSubmit={onSectionFormSubmit(handleSubmitSection)} id="dialog-section-form">
                <Dialog
                    open={sectionDialogOpen}
                    title={strings.menuSectionEditTitle.replace("{{name}}", sectionDialogTitleName ?? "")}
                    formId="dialog-section-form"
                    formIsDirty={sectionFormState.isDirty}
                    saving={sectionIsSaving}
                    saveButtonTitle={strings.save}
                    onCancel={() => setSectionDialogOpen(false)}
                    cancelButtonTitle={strings.cancel}
                >
                    {/* Setup TextField, the ...register function registers the field with react-hook-form (including validation) */}
                    <TextField
                        id="section-name"
                        label={strings.name}
                        variant="outlined"
                        sx={{ width: '400px' }}
                        {...registerSection("name", {
                            required: strings.validationNameRequired
                        })}
                        error={!!sectionFormState.errors.name}
                        helperText={sectionFormState.errors.name?.message || " "}
                    />
                </Dialog>
            </form>
        </>
    );
};

export default MenuList;
